"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { User, authService } from '@/services/AuthService';
import { useCurrentUser, authKeys } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { transientRetryOptions } from '@adrodd/core';

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  /** True when the /current_user check failed with a network/server error (not a 401/403). */
  isNetworkError: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  /** Can use the author portal — authors and admins. */
  isAuthor: boolean;
  /** Can use the reviewer portal — reviewers and admins. */
  isReviewer: boolean;
  signIn: (email: string, password: string, honeypot?: string) => Promise<User>;
  signUp: (email: string, password: string, name?: string, honeypot?: string, termsAccepted?: boolean) => Promise<User>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  signInWithProvider: (provider: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();

  // Bridge variable: setQueryData updates the TanStack Query cache immediately,
  // but the query observer defers notification to useQuery subscribers via a
  // microtask (NotifyManager.scheduleMicrotask). If navigate() fires before
  // that microtask, AuthenticatedLayout reads stale isAuthenticated=false and
  // redirects to login. This bridge provides the user synchronously via React
  // setState so the auth state is available on the very next render, before
  // TanStack Query's observer catches up.
  const [immediateUser, setImmediateUser] = useState<User | null>(null);

  const { data: queryUser = null, isLoading, isError: isQueryError } = useCurrentUser();

  // A network/server error is distinct from "not authenticated" (401/403).
  // getCurrentUser returns null for auth errors and throws for network errors,
  // so isQueryError===true means the API was unreachable after all retries.
  const isNetworkError = isQueryError;

  // Use immediateUser as a bridge until useCurrentUser catches up
  const user = immediateUser ?? queryUser;

  // Clear the bridge once the query observer has caught up
  useEffect(() => {
    if (queryUser !== null && immediateUser !== null) {
      setImmediateUser(null);
    }
  }, [queryUser, immediateUser]);

  // When the API is unreachable after all retries, do NOT clear the stored
  // token — a transient blip (slow cold start, brief server restart) must not
  // log the user out. The token is preserved so the next reload re-authenticates
  // once the server is back; we only surface a non-destructive notice.
  useEffect(() => {
    if (isNetworkError) {
      toast.info('Server unreachable, retrying. You\'ll stay signed in once it\'s back.');
    }
  }, [isNetworkError]);

  const signInMutation = useMutation({
    mutationFn: ({ email, password, honeypot }: { email: string; password: string; honeypot?: string }) =>
      authService.signIn(email, password, honeypot),
    // One retry only: login is rate limited to 5 per 20s. A 401 never retries.
    ...transientRetryOptions(1),
    onSuccess: async (user) => {
      // Update cache immediately
      queryClient.setQueryData(authKeys.currentUser(), user);
      // Force a refetch to ensure we have the latest server state including any side-loaded data
      await queryClient.invalidateQueries({ queryKey: authKeys.currentUser() });
    },
    // No onError toast: LoginPage renders the failure inline. A toast here
    // would double up with that inline message (the exact bug reported).
  });

  const signUpMutation = useMutation({
    mutationFn: ({ email, password, name, honeypot, termsAccepted }: { email: string; password: string; name?: string; honeypot?: string; termsAccepted?: boolean }) =>
      authService.signUp(email, password, name, honeypot, termsAccepted),
    onSuccess: (user) => {
      queryClient.setQueryData(authKeys.currentUser(), user);
    },
    // No onError toast: SignupPage renders the failure inline (same reason as signIn above).
  });

  const signOutMutation = useMutation({
    mutationFn: () => authService.signOut(),
    onSuccess: () => {
      queryClient.setQueryData(authKeys.currentUser(), null);
      queryClient.invalidateQueries();
    },
    onError: (error: unknown) => {
      toast.error('Failed to sign out');
      console.warn('Sign out error:', error);
    },
  });

  const signIn = async (email: string, password: string, honeypot?: string) => {
    const user = await signInMutation.mutateAsync({ email, password, honeypot });
    setImmediateUser(user);
    return user;
  };

  const signUp = async (email: string, password: string, name?: string, honeypot?: string, termsAccepted?: boolean) => {
    const user = await signUpMutation.mutateAsync({ email, password, name, honeypot, termsAccepted });
    setImmediateUser(user);
    return user;
  };

  const signOut = async () => {
    await signOutMutation.mutateAsync();
  };

  const refreshUser = async () => {
    await queryClient.refetchQueries({ queryKey: authKeys.currentUser() });
  };

  const signInWithProvider = async (provider: string) => {
    await authService.signInWithOAuth(provider as 'google' | 'microsoft' | 'discord');
  };

  const value: AuthContextType = {
    user,
    loading: isLoading,
    isNetworkError,
    isAuthenticated: user !== null,
    isAdmin: user?.admin ?? false,
    isAuthor: user?.author ?? user?.role === 'author',
    isReviewer: user?.reviewer ?? false,
    signIn,
    signUp,
    signOut,
    refreshUser,
    signInWithProvider,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
